import { clearAllFiles, getAllFiles, saveFile, saveProjectRootHandle, type StoredFileRecord } from '../db/files-repo';
import { languageForPath } from '../util/language-for-path';
import { getGitHubSource, getGitHubToken, saveGitHubSource, saveGitHubToken } from '../db/github-repo';
import { commitFile, getDefaultBranch, getFileContent, listRepoFiles } from '../github/client';
import { mapWithConcurrency } from '../util/concurrency';
import { contentHash } from '../nlp/content-hash';
import { openModal } from './modal';
import { icon } from './icons';

const MAX_FILE_BYTES = 512 * 1024; // skip anything that looks like a generated/binary blob
const SKIP_DIRS = new Set(['node_modules', '.git', 'dist', 'build', '.venv', '__pycache__']);

export interface FileExplorerCallbacks {
  onOpenFile: (path: string) => void;
  /**
   * Fires whenever the loaded file set changes (initial load, folder open, drop) — used to
   * (re)build the project-wide symbol index (A2). `isNewProject` is true only when the user just
   * replaced the workspace with a different folder (open/drop), not when resuming the same one on
   * page load — the caller uses it to reset per-project state like open editor tabs and the AI's
   * working set of open files, which would otherwise silently accumulate stale content forever
   * across unrelated projects.
   */
  onFilesLoaded: (files: StoredFileRecord[], isNewProject: boolean) => void;
}

export class FileExplorer {
  private el: HTMLElement;
  private activePath: string | null = null;
  private callbacks: FileExplorerCallbacks;

  constructor(container: HTMLElement, callbacks: FileExplorerCallbacks) {
    this.el = container;
    this.callbacks = callbacks;
    this.render([], false);
    void this.loadPersisted();
  }

  private async loadPersisted() {
    const files = await getAllFiles();
    if (files.length) this.render(files, false);
  }

  private async openViaFileSystemAccess() {
    if (!('showDirectoryPicker' in window)) {
      alert('File System Access API is not available in this browser — drag and drop a folder onto the panel instead.');
      return;
    }
    try {
      // showDirectoryPicker isn't in the default lib.dom types yet in all TS versions.
      const handle: FileSystemDirectoryHandle = await (window as unknown as { showDirectoryPicker: () => Promise<FileSystemDirectoryHandle> }).showDirectoryPicker();
      await clearAllFiles();
      await saveGitHubSource(null); // a local folder replaces any previously-loaded GitHub repo
      await saveProjectRootHandle(handle);
      const records: StoredFileRecord[] = [];
      await this.walkDirectoryHandle(handle, '', records);
      for (const r of records) await saveFile(r);
      this.render(records, true);
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') console.warn('Directory picker failed', err);
    }
  }

  private async walkDirectoryHandle(
    dir: FileSystemDirectoryHandle,
    prefix: string,
    out: StoredFileRecord[],
  ): Promise<void> {
    for await (const [name, handle] of (dir as unknown as AsyncIterable<[string, FileSystemHandle]>)) {
      if (handle.kind === 'directory') {
        if (SKIP_DIRS.has(name)) continue;
        await this.walkDirectoryHandle(handle as FileSystemDirectoryHandle, `${prefix}${name}/`, out);
      } else {
        const fileHandle = handle as FileSystemFileHandle;
        const file = await fileHandle.getFile();
        if (file.size > MAX_FILE_BYTES) continue;
        const path = `${prefix}${name}`;
        const languageId = languageForPath(path);
        if (!languageId) continue; // only load files the pipeline can actually parse for now
        const content = await file.text();
        out.push({ path, content, languageId, handle: fileHandle, updatedAt: Date.now() });
      }
    }
  }

  private setupDropZone(zone: HTMLElement) {
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('dragover');
    });
    zone.addEventListener('dragleave', () => zone.classList.remove('dragover'));
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('dragover');
      void this.handleDrop(e.dataTransfer);
    });
  }

  private async handleDrop(dataTransfer: DataTransfer | null) {
    if (!dataTransfer) return;
    const items = Array.from(dataTransfer.items);
    const records: StoredFileRecord[] = [];
    const entries = items
      .map((item) => (item as unknown as { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.())
      .filter((e): e is FileSystemEntry => Boolean(e));

    for (const entry of entries) await this.walkEntry(entry, '', records);
    if (records.length) {
      await clearAllFiles();
      await saveGitHubSource(null); // a dropped folder replaces any previously-loaded GitHub repo
      for (const r of records) await saveFile(r);
      this.render(records, true);
    }
  }

  private async walkEntry(entry: FileSystemEntry, prefix: string, out: StoredFileRecord[]): Promise<void> {
    if (entry.isDirectory) {
      if (SKIP_DIRS.has(entry.name)) return;
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const children = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      for (const child of children) await this.walkEntry(child, `${prefix}${entry.name}/`, out);
    } else {
      const path = `${prefix}${entry.name}`;
      const languageId = languageForPath(path);
      if (!languageId) return;
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      if (file.size > MAX_FILE_BYTES) return;
      const content = await file.text();
      out.push({ path, content, languageId, updatedAt: Date.now() });
    }
  }

  /** Fetches every matching file's content and replaces the workspace with this repo/branch's files. */
  private async loadGitHubRepo(owner: string, repo: string, branchInput: string, token: string): Promise<void> {
    await saveGitHubToken(token);
    const branch = branchInput.trim() || (await getDefaultBranch(token, owner, repo));
    const entries = await listRepoFiles(token, owner, repo, branch);
    const records = await mapWithConcurrency(entries, 6, async (entry): Promise<StoredFileRecord> => {
      const { content, sha } = await getFileContent(token, owner, repo, entry.path, branch);
      return {
        path: entry.path,
        content,
        languageId: languageForPath(entry.path),
        githubSha: sha,
        githubSyncedHash: await contentHash(content),
        updatedAt: Date.now(),
      };
    });

    await clearAllFiles();
    await saveGitHubSource({ owner, repo, branch });
    for (const r of records) await saveFile(r);
    this.render(records, true);
  }

  /**
   * Commits every GitHub-sourced file whose content has changed since its last fetch/commit.
   * Re-reads from IndexedDB rather than any in-memory list, since edits land there directly
   * (via the editor's onChange) without going through FileExplorer at all.
   */
  private async commitToGitHub(button: HTMLButtonElement): Promise<void> {
    const source = await getGitHubSource();
    const token = await getGitHubToken();
    if (!source || !token) return;

    const allFiles = await getAllFiles();
    const dirty: { file: StoredFileRecord; hash: string; sha: string }[] = [];
    for (const file of allFiles) {
      if (!file.githubSha) continue;
      const hash = await contentHash(file.content);
      if (hash !== file.githubSyncedHash) dirty.push({ file, hash, sha: file.githubSha });
    }

    if (dirty.length === 0) {
      alert('No changes to commit.');
      return;
    }

    const message = prompt(`Commit message for ${dirty.length} file(s):`, `Update ${dirty.length} file(s) via Naulon`);
    if (!message) return;

    const buttonHtml = button.innerHTML;
    button.disabled = true;
    button.textContent = 'Committing…';
    const failures: string[] = [];
    for (const { file, hash, sha } of dirty) {
      try {
        const result = await commitFile(token, source.owner, source.repo, file.path, file.content, sha, source.branch, message);
        await saveFile({ ...file, githubSha: result.sha, githubSyncedHash: hash });
      } catch (err) {
        failures.push(`${file.path}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    button.disabled = false;
    button.innerHTML = buttonHtml;
    void this.refreshDirtyMarkers();

    alert(
      failures.length
        ? `Committed ${dirty.length - failures.length} file(s). Failed:\n${failures.join('\n')}`
        : `Committed ${dirty.length} file(s) to GitHub.`,
    );
  }

  private openGitHubModal() {
    void (async () => {
      const [source, token] = await Promise.all([getGitHubSource(), getGitHubToken()]);
      const modal = openModal('Connect GitHub repo');
      const body = modal.bodyEl;

      const repoInput = document.createElement('input');
      repoInput.type = 'text';
      repoInput.placeholder = 'owner/repo';
      repoInput.value = source ? `${source.owner}/${source.repo}` : '';
      body.appendChild(this.labeledRow('Repository', repoInput));

      const branchInput = document.createElement('input');
      branchInput.type = 'text';
      branchInput.placeholder = 'main (blank = default branch)';
      branchInput.value = source?.branch ?? '';
      body.appendChild(this.labeledRow('Branch', branchInput));

      const tokenInput = document.createElement('input');
      tokenInput.type = 'password';
      tokenInput.placeholder = 'ghp_… — stored only in this browser';
      tokenInput.value = token;
      body.appendChild(this.labeledRow('Personal access token', tokenInput));

      const statusEl = document.createElement('div');
      statusEl.className = 'account-status error';
      statusEl.style.display = 'none';
      body.appendChild(statusEl);

      const btnRow = document.createElement('div');
      btnRow.className = 'account-row';
      const loadBtn = document.createElement('button');
      loadBtn.className = 'btn primary';
      loadBtn.textContent = 'Load repo';
      const cancelBtn = document.createElement('button');
      cancelBtn.className = 'btn subtle';
      cancelBtn.textContent = 'Cancel';
      cancelBtn.addEventListener('click', () => modal.close());
      btnRow.appendChild(loadBtn);
      btnRow.appendChild(cancelBtn);
      body.appendChild(btnRow);

      const showError = (message: string) => {
        statusEl.textContent = message;
        statusEl.style.display = '';
      };

      loadBtn.addEventListener('click', () => {
        void (async () => {
          const [owner, repo] = repoInput.value.trim().split('/').map((s) => s.trim());
          if (!owner || !repo) return showError('Enter a repo as "owner/repo".');
          const tokenValue = tokenInput.value.trim();
          if (!tokenValue) return showError('A personal access token is required.');

          statusEl.style.display = 'none';
          loadBtn.disabled = true;
          loadBtn.textContent = 'Loading…';
          try {
            await this.loadGitHubRepo(owner, repo, branchInput.value, tokenValue);
            modal.close();
          } catch (err) {
            showError(err instanceof Error ? err.message : String(err));
            loadBtn.disabled = false;
            loadBtn.textContent = 'Load repo';
          }
        })();
      });
    })();
  }

  private labeledRow(labelText: string, input: HTMLInputElement): HTMLElement {
    const row = document.createElement('div');
    row.className = 'account-row';
    const label = document.createElement('label');
    label.textContent = labelText;
    label.style.minWidth = '140px';
    input.style.flex = '1';
    row.appendChild(label);
    row.appendChild(input);
    return row;
  }

  setActivePath(path: string) {
    this.activePath = path;
    this.el.querySelectorAll('.tree-item').forEach((n) => n.classList.toggle('active', n.getAttribute('data-path') === path));
  }

  private render(files: StoredFileRecord[], isNewProject: boolean) {
    this.callbacks.onFilesLoaded(files, isNewProject);
    this.el.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'pane-header';
    header.innerHTML = `<span class="pane-title">Files</span>${
      files.length ? `<span class="pane-header-count mono">${files.length}</span>` : ''
    }`;
    this.el.appendChild(header);

    const actions = document.createElement('div');
    actions.className = 'actions';
    const openBtn = document.createElement('button');
    openBtn.className = 'btn subtle';
    openBtn.innerHTML = `${icon('folder')}<span>Open folder</span>`;
    openBtn.addEventListener('click', () => void this.openViaFileSystemAccess());
    actions.appendChild(openBtn);

    const githubBtn = document.createElement('button');
    githubBtn.className = 'btn subtle';
    githubBtn.innerHTML = `${icon('github')}<span>Repo</span>`;
    githubBtn.addEventListener('click', () => this.openGitHubModal());
    actions.appendChild(githubBtn);

    const commitBtn = document.createElement('button');
    commitBtn.className = 'btn subtle';
    commitBtn.innerHTML = `${icon('commit')}<span>Commit</span>`;
    commitBtn.style.display = 'none';
    commitBtn.addEventListener('click', () => void this.commitToGitHub(commitBtn));
    actions.appendChild(commitBtn);
    void getGitHubSource().then((source) => {
      if (source) commitBtn.style.display = '';
    });

    this.el.appendChild(actions);

    if (files.length === 0) {
      const dropZone = document.createElement('div');
      dropZone.className = 'drop-zone';
      dropZone.textContent = 'Drag & drop a project folder here, or use "Open folder" above.';
      this.setupDropZone(dropZone);
      this.el.appendChild(dropZone);
      return;
    }

    const tree = document.createElement('div');
    tree.className = 'tree';
    this.setupDropZone(tree);

    const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path));
    for (const file of sorted) {
      const item = document.createElement('div');
      const depth = file.path.split('/').length - 1;
      item.className = 'tree-item' + (file.path === this.activePath ? ' active' : '');
      item.style.paddingLeft = `${10 + depth * 12}px`;
      item.setAttribute('data-path', file.path);
      const name = document.createElement('span');
      name.className = 'tree-name';
      name.textContent = file.path.split('/').pop() ?? file.path;
      item.appendChild(name);
      const marker = document.createElement('span');
      marker.className = 'tree-dirty';
      item.appendChild(marker);
      item.title = file.path;
      item.addEventListener('click', () => {
        this.setActivePath(file.path);
        this.callbacks.onOpenFile(file.path);
      });
      tree.appendChild(item);
    }
    this.el.appendChild(tree);
    void this.refreshDirtyMarkers();
  }

  /**
   * Marks GitHub-sourced files whose content no longer matches what was last fetched/committed.
   * Re-read from IndexedDB rather than the rendered list, since editor keystrokes save straight
   * there without going through this component.
   */
  private async refreshDirtyMarkers(): Promise<void> {
    const tree = this.el.querySelector('.tree');
    if (!tree) return;
    const files = await getAllFiles();
    for (const file of files) {
      if (!file.githubSha) continue;
      const item = tree.querySelector<HTMLElement>(`.tree-item[data-path="${cssEscape(file.path)}"]`);
      if (!item) continue;
      const dirty = (await contentHash(file.content)) !== file.githubSyncedHash;
      item.classList.toggle('dirty', dirty);
      const marker = item.querySelector('.tree-dirty');
      if (marker) marker.textContent = dirty ? '●' : '';
      if (dirty) item.title = `${file.path} — uncommitted changes`;
    }
  }
}

function cssEscape(value: string): string {
  const escaper = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS?.escape;
  return escaper ? escaper(value) : value.replace(/["\\]/g, '\\$&');
}
